// SyndicationTab — the new "Syndication" tab on the Deal Detail page.
//
// Two main states:
//   • Empty: no partnership yet for this deal — sponsor sees a centered
//     "Syndicate this deal" card with a "Create Partnership" CTA.
//   • Populated: cap table with all members + totals + a sponsor action bar
//     (Invite Co-Investor, Issue Capital Call). Non-sponsor parties see the
//     cap table read-only.
//
// All interactive elements have `data-testid` hooks so the e2e tester can
// drive the flow without hunting through DOM text. The tab is wrapped by
// <TabErrorBoundary tabName="Syndication"> on Deal Detail, so a render
// crash here can no longer take down the rest of the page.

import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Skeleton from "@/components/ui/Skeleton";
import { toast } from "sonner";
import {
  Building2,
  Loader2,
  Plus,
  Send,
  Users,
  Wallet,
  Sparkles,
  HandCoins,
} from "lucide-react";
import api from "@/lib/api";
import { useFmt } from "@/lib/useFmt";
import { useAuth } from "@/contexts/AuthContext";
import DistributionsSection from "@/components/deal/DistributionsSection";
import CapTablePieChart from "@/components/syndication/CapTablePieChart";
import DistributionSparkline from "@/components/syndication/DistributionSparkline";

function CreatePartnershipModal({ open, onClose, dealId, onCreated }) {
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [sponsorPct, setSponsorPct] = useState("0");
  const [sponsorCommitted, setSponsorCommitted] = useState("0");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !target) return;
    setBusy(true);
    try {
      const { data } = await api.post("/syndication/partnerships", {
        deal_id: dealId,
        name: name.trim(),
        total_equity_target: Number(target),
        sponsor_ownership_pct: Number(sponsorPct) || 0,
        sponsor_committed_amount: Number(sponsorCommitted) || 0,
      });
      toast.success("Partnership created");
      onCreated(data);
      onClose();
      setName("");
      setTarget("");
      setSponsorPct("0");
      setSponsorCommitted("0");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not create partnership";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="synd-create-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create partnership</DialogTitle>
          <DialogDescription>
            Set up a syndication for this deal. You'll be added as the sponsor.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="synd-create-name">Partnership name</Label>
            <Input
              id="synd-create-name"
              data-testid="synd-create-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. 9143 SW 77th Ave LLC"
              required
            />
          </div>
          <div>
            <Label htmlFor="synd-create-target">Total equity target (USD)</Label>
            <Input
              id="synd-create-target"
              data-testid="synd-create-target-input"
              type="number"
              min="0"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="250000"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
            <div>
              <Label htmlFor="synd-create-sponsor-pct" className="text-xs">My ownership %</Label>
              <Input
                id="synd-create-sponsor-pct"
                data-testid="synd-create-sponsor-pct-input"
                type="number" min="0" max="100" step="0.01"
                value={sponsorPct}
                onChange={(e) => setSponsorPct(e.target.value)}
                placeholder="e.g. 20"
              />
            </div>
            <div>
              <Label htmlFor="synd-create-sponsor-committed" className="text-xs">My capital ($)</Label>
              <Input
                id="synd-create-sponsor-committed"
                data-testid="synd-create-sponsor-committed-input"
                type="number" min="0"
                value={sponsorCommitted}
                onChange={(e) => setSponsorCommitted(e.target.value)}
                placeholder="e.g. 20000"
              />
            </div>
            <p className="col-span-2 text-xs text-slate-500">
              Leave at 0 if you're not putting capital into this deal yourself.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              data-testid="synd-create-submit-btn"
              className="bg-indigo-600 text-white hover:bg-indigo-700"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Create partnership
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InviteMemberModal({ open, onClose, partnershipId, onInvited }) {
  const [email, setEmail] = useState("");
  const [pct, setPct] = useState("");
  const [committed, setCommitted] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/syndication/partnerships/${partnershipId}/members/invite`, {
        email: email.trim(),
        ownership_pct: Number(pct),
        committed_amount: Number(committed),
        display_name: name.trim() || null,
      });
      toast.success(`Invite sent to ${email}`);
      onInvited();
      onClose();
      setEmail(""); setPct(""); setCommitted(""); setName("");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send invite";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="synd-invite-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite co-investor</DialogTitle>
          <DialogDescription>
            They'll receive an email and need to log in to accept their slot.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="synd-invite-email">Email</Label>
            <Input
              id="synd-invite-email" type="email" required
              data-testid="synd-invite-email-input"
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="lp@example.com"
            />
          </div>
          <div>
            <Label htmlFor="synd-invite-name">Display name (optional)</Label>
            <Input
              id="synd-invite-name"
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Jane Q. Investor"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="synd-invite-pct">Ownership %</Label>
              <Input
                id="synd-invite-pct" type="number" required min="0" max="100" step="0.01"
                data-testid="synd-invite-ownership-input"
                value={pct} onChange={(e) => setPct(e.target.value)}
                placeholder="25"
              />
            </div>
            <div>
              <Label htmlFor="synd-invite-committed">Committed (USD)</Label>
              <Input
                id="synd-invite-committed" type="number" required min="0"
                data-testid="synd-invite-committed-input"
                value={committed} onChange={(e) => setCommitted(e.target.value)}
                placeholder="50000"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit" disabled={busy}
              data-testid="synd-invite-submit-btn"
              className="bg-indigo-600 text-white hover:bg-indigo-700"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Send invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function IssueCapitalCallModal({ open, onClose, partnershipId, onIssued }) {
  const [amount, setAmount] = useState("");
  const [due, setDue] = useState("");
  const [reason, setReason] = useState("initial_close");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/syndication/partnerships/${partnershipId}/capital-calls`, {
        amount: Number(amount),
        due_date: new Date(due).toISOString(),
        reason,
      });
      toast.success("Capital call issued");
      onIssued();
      onClose();
      setAmount(""); setDue("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not issue capital call");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="synd-issue-call-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Issue capital call</DialogTitle>
          <DialogDescription>
            Members will be notified that funds are due.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="cc-amount">Amount (USD)</Label>
            <Input id="cc-amount" type="number" required min="1"
              value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="cc-due">Due date</Label>
            <Input id="cc-due" type="date" required
              value={due} onChange={(e) => setDue(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="cc-reason">Reason</Label>
            <select
              id="cc-reason"
              className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
              value={reason} onChange={(e) => setReason(e.target.value)}
            >
              <option value="initial_close">Initial close</option>
              <option value="rehab_draw">Rehab draw</option>
              <option value="operating_shortfall">Operating shortfall</option>
            </select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy}
              className="bg-indigo-600 text-white hover:bg-indigo-700"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wallet className="mr-2 h-4 w-4" />}
              Issue call
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CapTable({ data, fmt }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
      <table data-testid="synd-cap-table" className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-slate-50">
          <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <th className="px-4 py-3">Member</th>
            <th className="px-4 py-3">Role</th>
            <th className="px-4 py-3 text-right">Ownership %</th>
            <th className="px-4 py-3 text-right">Committed</th>
            <th className="px-4 py-3 text-right">Contributed</th>
            <th className="px-4 py-3 text-right">Pref Paid</th>
            <th className="px-4 py-3 text-right">Capital Returned</th>
            <th className="px-4 py-3">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {data.members.map((m) => (
            <tr
              key={m.member_id}
              data-testid={`synd-member-row-${m.slug}`}
              className="text-slate-800"
            >
              <td className="px-4 py-3">
                <div className="font-medium text-slate-900">{m.display_name || m.email}</div>
                {m.display_name ? (
                  <div className="text-xs text-slate-500">{m.email}</div>
                ) : null}
              </td>
              <td className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">{m.role}</td>
              <td className="px-4 py-3 text-right tabular-nums">{m.ownership_pct.toFixed(2)}%</td>
              <td className="px-4 py-3 text-right tabular-nums">{fmt.money(m.committed)}</td>
              <td className="px-4 py-3 text-right tabular-nums">{fmt.money(m.contributed_total)}</td>
              <td className="px-4 py-3 text-right tabular-nums">{fmt.money(m.cumulative_pref_paid || 0)}</td>
              <td className="px-4 py-3 text-right tabular-nums">{fmt.money(m.cumulative_capital_returned || 0)}</td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  m.invite_status === "accepted" ? "bg-emerald-50 text-emerald-700"
                  : m.invite_status === "pending" ? "bg-amber-50 text-amber-700"
                  : "bg-slate-50 text-slate-600"
                }`}>
                  {m.invite_status}
                </span>
              </td>
            </tr>
          ))}
          <tr data-testid="synd-totals-row" className="bg-slate-50/60 font-semibold text-slate-900">
            <td className="px-4 py-3" colSpan={2}>Totals</td>
            <td className="px-4 py-3 text-right tabular-nums">{data.totals.ownership_pct.toFixed(2)}%</td>
            <td className="px-4 py-3 text-right tabular-nums">{fmt.money(data.totals.committed)}</td>
            <td className="px-4 py-3 text-right tabular-nums">{fmt.money(data.totals.contributed)}</td>
            <td className="px-4 py-3 text-right tabular-nums">
              {fmt.money(data.members.reduce((s, m) => s + (m.cumulative_pref_paid || 0), 0))}
            </td>
            <td className="px-4 py-3 text-right tabular-nums">
              {fmt.money(data.members.reduce((s, m) => s + (m.cumulative_capital_returned || 0), 0))}
            </td>
            <td className="px-4 py-3 text-right text-xs text-slate-500">
              target {fmt.money(data.totals.target)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function SyndicationTab({ deal }) {
  const fmt = useFmt();
  const { user } = useAuth();
  const [partnership, setPartnership] = useState(null);
  const [capTable, setCapTable] = useState(null);
  const [distributionsForSparkline, setDistributionsForSparkline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showCall, setShowCall] = useState(false);

  const refresh = useCallback(async (pid) => {
    if (!pid) return;
    const { data } = await api.get(`/syndication/partnerships/${pid}/cap-table`);
    setCapTable(data);
    setPartnership(data.partnership);
    // Phase 34c.3 — pull historical runs for the sparkline. Best-effort:
    // the sparkline handles an empty list gracefully via its own placeholder.
    try {
      const { data: runs } = await api.get(`/syndication/partnerships/${pid}/distributions`);
      setDistributionsForSparkline(runs || []);
    } catch {
      setDistributionsForSparkline([]);
    }
  }, []);

  // On mount: find the partnership for this deal (if any)
  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      try {
        const { data: list } = await api.get("/syndication/partnerships");
        const mine = (list || []).find((p) => p.deal_id === deal.id);
        if (!alive) return;
        if (mine) {
          await refresh(mine.id);
        } else {
          setPartnership(null);
          setCapTable(null);
        }
      } catch {
        // best-effort — leave in empty state
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [deal.id, refresh]);

  const isSponsor =
    partnership && user && partnership.sponsor_user_id === user.id;

  if (loading) {
    // Phase 34c.3 polish — skeletons instead of a spinner so the layout
    // doesn't pop on first paint.
    return (
      <div className="space-y-4" data-testid="synd-loading-skeleton">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
            <Skeleton className="h-5 w-32 bg-slate-100" />
            <div className="mt-4 space-y-2">
              <Skeleton className="h-4 w-full bg-slate-100" />
              <Skeleton className="h-4 w-full bg-slate-100" />
              <Skeleton className="h-4 w-3/4 bg-slate-100" />
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <Skeleton className="h-4 w-20 bg-slate-100" />
            <Skeleton className="mx-auto mt-6 h-40 w-40 rounded-full bg-slate-100" />
            <Skeleton className="mt-6 h-3 w-3/4 bg-slate-100" />
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <Skeleton className="h-4 w-32 bg-slate-100" />
          <Skeleton className="mt-4 h-32 w-full bg-slate-100" />
        </div>
      </div>
    );
  }

  if (!partnership) {
    return (
      <>
        <div
          data-testid="synd-empty-state"
          className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center"
        >
          <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
            <Users className="h-6 w-6" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900">Syndicate this deal</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
            Invite co-investors, split ownership, and track capital calls in one
            place. The cap-table math runs pro-rata — waterfall &amp; pref-return
            arrive in the next release.
          </p>
          <Button
            onClick={() => setShowCreate(true)}
            data-testid="synd-create-partnership-btn"
            className="mt-6 bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
          >
            <Plus className="mr-2 h-4 w-4" /> Create partnership
          </Button>
        </div>
        <CreatePartnershipModal
          open={showCreate}
          onClose={() => setShowCreate(false)}
          dealId={deal.id}
          onCreated={(p) => refresh(p.id)}
        />
      </>
    );
  }

  return (
    <div className="space-y-6" data-testid="synd-tab-content">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
            <Building2 className="h-4 w-4" /> Partnership
          </div>
          <h3 className="mt-1 text-xl font-semibold text-slate-900">{partnership.name}</h3>
          <p className="text-xs text-slate-500">
            Status:{" "}
            <span className="font-medium uppercase tracking-wider text-slate-700">
              {partnership.status}
            </span>
            {" · "}Target {fmt.money(partnership.total_equity_target)}
          </p>
        </div>
        {isSponsor && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowInvite(true)}
              data-testid="synd-invite-btn"
              className="border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              <Send className="mr-2 h-4 w-4" /> Invite co-investor
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowCall(true)}
              data-testid="synd-issue-call-btn"
              className="border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              <HandCoins className="mr-2 h-4 w-4" /> Issue capital call
            </Button>
          </div>
        )}
      </div>

      {capTable ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2"><CapTable data={capTable} fmt={fmt} /></div>
          <div className="lg:col-span-1">
            <CapTablePieChart members={capTable.members} />
          </div>
        </div>
      ) : null}

      {capTable && distributionsForSparkline ? (
        <DistributionSparkline runs={distributionsForSparkline} />
      ) : null}

      {capTable ? (
        <section
          data-testid="synd-pro-rata-preview"
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            Pro-rata distribution preview
          </h4>
          <p className="mt-1 text-xs text-slate-500">
            How each $1 of distributable cash would split today — pure
            ownership-% pro-rata. Waterfall &amp; pref-return arrive in 34b.
          </p>
          <ul className="mt-4 space-y-1 text-sm text-slate-700">
            {capTable.members.map((m) => {
              const ratio = capTable.pro_rata_distribution_preview[m.member_id] || 0;
              return (
                <li key={m.member_id} className="flex justify-between tabular-nums">
                  <span>{m.display_name || m.email}</span>
                  <span className="font-medium">{(ratio * 100).toFixed(2)}%</span>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <InviteMemberModal
        open={showInvite}
        onClose={() => setShowInvite(false)}
        partnershipId={partnership.id}
        onInvited={() => refresh(partnership.id)}
      />
      <IssueCapitalCallModal
        open={showCall}
        onClose={() => setShowCall(false)}
        partnershipId={partnership.id}
        onIssued={() => refresh(partnership.id)}
      />
      <DistributionsSection partnership={partnership} isSponsor={isSponsor} />
    </div>
  );
}
